import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button, Paper } from '@mui/material';
import { CheckCircle as SuccessIcon, Error as ErrorIcon } from '@mui/icons-material';
import { boardsApi } from '../../api';
import { useAuth } from '../../hooks';

/**
 * InviteJoinPage handles the invite link flow:
 * /boards/:boardIdOrSlug/invite/:inviteCode
 *
 * If authenticated: auto-joins the board and redirects to it
 * If not authenticated: redirects to login with return URL
 */
export const InviteJoinPage: React.FC = () => {
  const { boardIdOrSlug, inviteCode } = useParams<{ boardIdOrSlug: string; inviteCode: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Wait for auth to be determined
    if (authLoading) return;

    if (!inviteCode) {
      setStatus('error');
      setError('Invalid invite link - no invite code provided');
      return;
    }

    if (!isAuthenticated) {
      // Redirect to login with return URL
      const returnUrl = encodeURIComponent(window.location.pathname);
      navigate(`/login?redirect=${returnUrl}`, { replace: true });
      return;
    }

    // Try to join the board
    // The inviteCode parameter could be either:
    // 1. A board's inviteCode (short code like "ABC123") - uses boardsApi.join()
    // 2. An email invite token (long hex string) - uses boardsApi.acceptEmailInvite()
    const joinBoard = async () => {
      try {
        // First, try as a board invite code (the most common case for shared links)
        const board = await boardsApi.join(inviteCode);
        setStatus('success');
        setTimeout(() => {
          navigate(`/boards/${board.slug || board.id}`, { replace: true });
        }, 1500);
      } catch (_joinErr: unknown) {
        // If board join fails, try as an email invite token
        try {
          const result = await boardsApi.acceptEmailInvite(inviteCode);
          setStatus('success');
          setTimeout(() => {
            navigate(`/boards/${boardIdOrSlug || result.boardId}`, { replace: true });
          }, 1500);
        } catch (err: unknown) {
          const apiError = err as { status?: number; message?: string };
          setStatus('error');

          if (apiError.status === 404) {
            setError('This invite link is invalid or has expired.');
          } else if (apiError.status === 400) {
            // Could be expired, already used, or already on board
            const message = apiError.message || '';
            if (message.includes('expired')) {
              setError('This invite has expired. Please ask the board manager to send a new invitation.');
            } else if (message.includes('already been used')) {
              setError('This invite has already been used.');
            } else if (message.includes('already on this board') || message.includes('already a participant')) {
              setError('You are already a member of this board.');
              setTimeout(() => {
                navigate(`/boards/${boardIdOrSlug}`, { replace: true });
              }, 1500);
            } else {
              setError(message || 'Failed to join board. Please try again.');
            }
          } else {
            setError(apiError.message || 'Failed to join board. Please try again.');
          }
        }
      }
    };

    joinBoard();
  }, [inviteCode, boardIdOrSlug, isAuthenticated, authLoading, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3,
      }}
    >
      <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
        {status === 'loading' && (
          <>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">Joining board...</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Please wait while we add you to this board.
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <SuccessIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6">Successfully joined!</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Redirecting to board...
            </Typography>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h6">Unable to join</Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
              {error}
            </Typography>
            <Button variant="contained" onClick={() => navigate('/boards')}>
              Go to My Boards
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default InviteJoinPage;
